import { Localized } from '../schema/localized'
import { PageElement } from '../schema/elements'
import { WebsiteDocument } from '../schema/document'
import { MenuSnapshot } from './menu'

/**
 * Everything the renderer needs that is not in the document.
 *
 * These are seams, not conveniences. Asset ids become URLs differently in the
 * editor (a blob or a signed preview URL) and on a published page (a CDN path),
 * and button targets resolve against the site's routing, which the document does
 * not know about. Passing them in keeps the renderer free of both.
 */
export interface RenderContext {
  /** Language being rendered. */
  language: string
  /** Falls back to this wherever `language` has nothing. */
  defaultLanguage: string
  mode: 'edit' | 'view'
  resolveAssetUrl: (assetId: string) => string
  resolveHref: (element: Extract<PageElement, { type: 'button' }>) => string
  /**
   * The one image allowed to load eagerly.
   *
   * Everything else is lazy, but the largest element above the fold must not be,
   * or the browser discovers it late and LCP pays for it. The renderer picks it
   * rather than the document, because it depends on order, not on intent.
   */
  priorityImageId?: string
  /**
   * Where the booking form posts (`urlForBooking`). A seam like the two above:
   * the document knows the venue's rooms, not the venue's address on the web.
   * The editor leaves it out, and its preview never submits anyway.
   */
  bookingAction?: string
  /**
   * The venue's menu as it stands (`render/menu.ts`). Resolved at publish
   * against the public menu endpoints and in the editor through the RMS;
   * never stored in the document, so the page never shows old prices.
   */
  menu?: MenuSnapshot | null
  /**
   * Today, as `YYYY-MM-DD`, for the booking form's date field.
   *
   * A seam and not a `new Date()` in the renderer, for two reasons. The
   * renderer is the same code in three places and only one of them knows what
   * "today" means to the visitor: the editor's preview is live, the prerender
   * is the day the page was built, and a test wants a date it chose. And a
   * renderer that reads the clock cannot be compared against its own output.
   *
   * Left out, the date field opens empty, which is what a browser does anyway
   * when it has nothing: the picker still lands on the current month.
   */
  today?: string
  /**
   * What stands in for a block still waiting for its data, in the editor's
   * views only: a map with no Google place shows where it would go and a way
   * to connect one. The words arrive translated, since the renderer has no
   * i18next. Left out, as on a published page, such a block and its row are
   * not drawn at all.
   */
  placeholders?: {
    map: { text: string; action: string; onAction: () => void }
  }
}

/**
 * One translatable value, in the language being rendered.
 *
 * Falls back to the site's default language rather than rendering nothing: a
 * missing translation should read as untranslated, not as a hole in the page.
 * Publication has its own, stricter opinion about how much may be missing.
 */
export function pick(field: Localized<string> | undefined, ctx: RenderContext) {
  if (!field) return ''
  return field[ctx.language] ?? field[ctx.defaultLanguage] ?? ''
}

export function pickValue<T>(
  field: Localized<T> | undefined,
  ctx: RenderContext
): T | undefined {
  if (!field) return undefined
  return field[ctx.language] ?? field[ctx.defaultLanguage]
}

/** The first image on a page, which is the one that gets to load eagerly. */
export function findPriorityImageId(
  page: WebsiteDocument['pages'][number]
): string | undefined {
  return page.blocks
    .filter(row => !row.hidden)
    .flatMap(row => row.columns)
    .flatMap(column => column.items)
    .find(item => item.type === 'image' || item.type === 'gallery')?.id
}
